import React, { useEffect, useState } from 'react';
import { supabase } from '../../../supabaseClient';
import trainingsDataRaw from '../../../data/trainings.json';

export default function TrainingsTab() {
    const [activeInnerTab, setActiveInnerTab] = useState('trainings'); // 'trainings', 'page'
    
    // Data states
    const [trainings, setTrainings] = useState([]);
    const [pageContent, setPageContent] = useState(null);
    const [loading, setLoading] = useState(true);

    // Form states
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [error, setError] = useState(null);

    // JSON Edit Mode
    const [showAdvancedJSON, setShowAdvancedJSON] = useState(false);
    const [jsonString, setJsonString] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [tRes, pRes] = await Promise.all([
                supabase.from('trainings').select('*').order('created_at', { ascending: false }),
                supabase.from('trainings_page_content').select('*').eq('page_name', 'trainings-page').single()
            ]);
            
            if (!tRes.error) setTrainings(tRes.data || []);
            if (!pRes.error) setPageContent(pRes.data?.data || null);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleImportJSON = async () => {
        if (!window.confirm("This will import data from trainings.json into your database. Existing data might be duplicated. Proceed?")) return;
        
        try {
            // 1. Import Page Content
            const { error: pError } = await supabase.from('trainings_page_content').upsert({
                page_name: 'trainings-page',
                data: trainingsDataRaw.page
            }, { onConflict: 'page_name' });
            if (pError) throw pError;

            // 2. Import Trainings
            for (let i = 0; i < trainingsDataRaw.trainings.length; i++) {
                const tr = trainingsDataRaw.trainings[i];
                
                // Keep the same structure, move 'id' to 'slug' conceptually
                const payload = {
                    slug: tr.id,
                    data: tr
                };

                const { error: tError } = await supabase.from('trainings').insert([payload]);
                // If it conflicts on slug, we could catch and update, but for simplicity let's just insert.
                if (tError && tError.code !== '23505') throw tError; // Ignore unique violation during bulk import
            }

            alert("Import successful!");
            fetchData();
        } catch (err) {
            console.error(err);
            alert("Error importing data: " + err.message);
        }
    };

    // --- TRAINING FUNCTIONS ---
    const handleAddTraining = () => {
        setFormData({ 
            id: null, 
            type: 'training', 
            slug: `training-${Math.random().toString(36).substr(2, 6)}`,
            data: {
                title: '',
                tagline: '',
                instructor: '',
                instructorImg: '',
                duration: '1 Day',
                level: 'Beginner',
                tags: [],
                description: '',
                seats: '',
                registerLink: '',
                abstract: [],
                outline: { "Day 1": [] },
                whoShouldAttend: [],
                whoShouldNot: [],
                audienceLevel: [],
                requirements: [],
                trainers: []
            } 
        });
        setIsEditing(true);
        setError(null);
        setUploadSuccess(false);
        setShowAdvancedJSON(false);
    };

    const handleEditTraining = (t) => {
        // Deep copy data so we don't mutate state directly
        setFormData({ 
            ...t, 
            type: 'training',
            data: JSON.parse(JSON.stringify(t.data))
        });
        setIsEditing(true);
        setError(null);
        setUploadSuccess(false);
        setShowAdvancedJSON(false);
    };

    const handleDeleteTraining = async (id) => {
        if (!window.confirm("Are you sure you want to delete this training?")) return;
        await supabase.from('trainings').delete().eq('id', id);
        fetchData();
    };

    // --- PAGE CONTENT FUNCTIONS ---
    const handleEditPage = () => {
        const pd = pageContent || {};
        setFormData({ 
            ...pd, 
            detailPage: pd.detailPage || {},
            listingPage: pd.listingPage || {},
            type: 'page' 
        });
        setIsEditing(true);
        setError(null);
    };

    // --- FORM SUBMISSION ---
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 500 * 1024) {
            alert('Image must be under 500KB');
            e.target.value = '';
            return;
        }

        setUploading(true);
        setError(null);
        setUploadSuccess(false);

        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;

        try {
            const { error: uploadError } = await supabase.storage
                .from('training-images')
                .upload(fileName, file);

            if (uploadError) throw uploadError;

            const { data } = supabase.storage
                .from('training-images')
                .getPublicUrl(fileName);

            setFormData({ 
                ...formData, 
                data: { ...formData.data, instructorImg: data.publicUrl } 
            });
            setUploadSuccess(true);
        } catch (err) {
            console.error("Upload error:", err);
            setError(err.message || 'Failed to upload image. Did you create the training-images bucket and set policies?');
        } finally {
            setUploading(false);
        }
    };

    // Helpers for multiline array inputs
    const handleArrayChange = (field, text) => {
        const arr = text.split('\n');
        setFormData({
            ...formData,
            data: { ...formData.data, [field]: arr }
        });
    };

    const getArrayText = (field) => {
        if (!formData.data || !Array.isArray(formData.data[field])) return '';
        return formData.data[field].join('\n');
    };

    // Helpers for Outline
    const handleOutlineDayChange = (dayName, text) => {
        const arr = text.split('\n');
        setFormData({
            ...formData,
            data: {
                ...formData.data,
                outline: {
                    ...(formData.data.outline || {}),
                    [dayName]: arr
                }
            }
        });
    };

    const addOutlineDay = () => {
        const currentOutline = formData.data.outline || {};
        const newDayNumber = Object.keys(currentOutline).length + 1;
        setFormData({
            ...formData,
            data: {
                ...formData.data,
                outline: {
                    ...currentOutline,
                    [`Day ${newDayNumber}`]: []
                }
            }
        });
    };

    // Helpers for Trainers
    const handleTrainerChange = (index, field, value) => {
        const newTrainers = [...(formData.data.trainers || [])];
        newTrainers[index] = { ...newTrainers[index], [field]: value };
        setFormData({ ...formData, data: { ...formData.data, trainers: newTrainers } });
    };

    const addTrainer = () => {
        const newTrainers = [...(formData.data.trainers || []), { name: 'New Trainer', title: '', bio: '', img: '' }];
        setFormData({ ...formData, data: { ...formData.data, trainers: newTrainers } });
    };

    const removeTrainer = (index) => {
        const newTrainers = [...(formData.data.trainers || [])];
        newTrainers.splice(index, 1);
        setFormData({ ...formData, data: { ...formData.data, trainers: newTrainers } });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            if (formData.type === 'training') {
                // Filter out empty lines before saving
                const payloadData = { ...formData.data };
                ['tags', 'abstract', 'whoShouldAttend', 'whoShouldNot', 'audienceLevel', 'requirements'].forEach(field => {
                    if (Array.isArray(payloadData[field])) {
                        payloadData[field] = payloadData[field].filter(s => s.trim() !== '');
                    }
                });

                if (payloadData.outline) {
                    const newOutline = {};
                    Object.entries(payloadData.outline).forEach(([day, items]) => {
                        if (Array.isArray(items)) {
                            newOutline[day] = items.filter(s => s.trim() !== '');
                        }
                    });
                    payloadData.outline = newOutline;
                }

                const payload = {
                    slug: formData.slug,
                    data: payloadData
                };
                // Make sure slug isn't empty
                if (!payload.slug) payload.slug = `training-${Date.now()}`;
                
                if (formData.id) await supabase.from('trainings').update(payload).eq('id', formData.id);
                else await supabase.from('trainings').insert([payload]);
            } else if (formData.type === 'page') {
                const payload = { ...formData };
                delete payload.type;
                const { error: pError } = await supabase.from('trainings_page_content').upsert({
                    page_name: 'trainings-page',
                    data: payload
                }, { onConflict: 'page_name' });
                if (pError) throw pError;
            }

            setIsEditing(false);
            fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) return <div className="text-white">Loading trainings...</div>;

    if (isEditing) {
        return (
            <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="card-header border-0 bg-transparent py-4 px-4">
                    <h4 className="text-white m-0" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 700 }}>
                        {formData.id ? `Edit ${formData.type}` : `Add New ${formData.type}`}
                    </h4>
                </div>
                <div className="card-body px-4 pb-4 text-white">
                    {error && <div className="alert alert-danger">{error}</div>}
                    <form onSubmit={handleSubmit}>
                        
                        {formData.type === 'training' && (
                            <div className="row g-4">
                                <div className="col-12 text-end">
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-warning"
                                        onClick={() => {
                                            if (!showAdvancedJSON) {
                                                setJsonString(JSON.stringify(formData.data, null, 2));
                                            }
                                            setShowAdvancedJSON(!showAdvancedJSON);
                                        }}
                                    >
                                        <i className="fas fa-code me-2"></i> {showAdvancedJSON ? "Hide Advanced JSON" : "Show Advanced JSON Editor"}
                                    </button>
                                </div>

                                {showAdvancedJSON ? (
                                    <div className="col-12">
                                        <div className="alert alert-warning mb-3">
                                            <strong>Warning:</strong> Be very careful when editing raw JSON. A syntax error will break the layout. This is recommended only for editing complex Outlines and Trainers.
                                        </div>
                                        <label className="form-label">Raw Data (JSON)</label>
                                        <textarea 
                                            className="form-control bg-dark text-warning" 
                                            style={{ border: '1px solid rgba(255,255,255,0.2)', fontFamily: 'monospace' }} 
                                            rows="20"
                                            value={jsonString}
                                            onChange={e => setJsonString(e.target.value)}
                                            onBlur={e => {
                                                try {
                                                    const parsed = JSON.parse(e.target.value);
                                                    setFormData({...formData, data: parsed});
                                                } catch(err) {
                                                    alert("Invalid JSON format! Please fix any missing quotes or commas before saving.");
                                                }
                                            }}
                                        ></textarea>
                                        <small className="text-white-50">Note: Modifying JSON directly requires valid syntax. We recommend editing via the UI.</small>
                                    </div>
                                ) : (
                                    <>
                                        <div className="col-md-6">
                                            <label className="form-label">Training URL Slug</label>
                                            <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} required value={formData.slug} onChange={e => setFormData({...formData, slug: e.target.value})} />
                                            <small className="text-white-50">e.g. web-app-pentesting (No spaces)</small>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Image (Max 500KB, Rec: 800x400px)</label>
                                            <div className="d-flex align-items-center gap-2">
                                                <input type="file" accept="image/*" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} onChange={handleImageUpload} disabled={uploading} />
                                                {uploading && <div className="spinner-border spinner-border-sm text-light" role="status"></div>}
                                            </div>
                                            {uploadSuccess && (
                                                <div className="mt-2 text-success" style={{ fontSize: '12px' }}>
                                                    <i className="fas fa-check-circle me-1"></i> Image uploaded successfully
                                                </div>
                                            )}
                                            {formData.data.instructorImg && !uploadSuccess && (
                                                <div className="mt-2">
                                                    <a href={formData.data.instructorImg} target="_blank" rel="noreferrer" style={{ color: 'var(--eventflow-primary)', fontSize: '12px', textDecoration: 'none' }}>
                                                        View current image <i className="fas fa-external-link-alt ms-1"></i>
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="col-12">
                                            <label className="form-label">Title</label>
                                            <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} required value={formData.data.title} onChange={e => setFormData({...formData, data: {...formData.data, title: e.target.value}})} />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label">Tagline (Short one-liner)</label>
                                            <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} required value={formData.data.tagline} onChange={e => setFormData({...formData, data: {...formData.data, tagline: e.target.value}})} />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label">Short Description (Shown on card)</label>
                                            <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="3" required value={formData.data.description} onChange={e => setFormData({...formData, data: {...formData.data, description: e.target.value}})}></textarea>
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label">Instructor Name</label>
                                            <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} required value={formData.data.instructor} onChange={e => setFormData({...formData, data: {...formData.data, instructor: e.target.value}})} />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">Duration</label>
                                            <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} required value={formData.data.duration} onChange={e => setFormData({...formData, data: {...formData.data, duration: e.target.value}})} placeholder="e.g. 2 Days" />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">Level</label>
                                            <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} required value={formData.data.level} onChange={e => setFormData({...formData, data: {...formData.data, level: e.target.value}})} placeholder="e.g. Intermediate" />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">Seats</label>
                                            <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.data.seats} onChange={e => setFormData({...formData, data: {...formData.data, seats: e.target.value}})} placeholder="e.g. 30 Seats" />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Registration Link (Leave blank for 'Coming Soon')</label>
                                            <input type="url" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.data.registerLink || ''} onChange={e => setFormData({...formData, data: {...formData.data, registerLink: e.target.value}})} />
                                        </div>

                                        <hr className="my-4 border-secondary w-100" />
                                        <h5 className="w-100 text-info">Detailed Content (One item per line)</h5>

                                        <div className="col-md-6">
                                            <label className="form-label">Tags</label>
                                            <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="3" value={getArrayText('tags')} onChange={e => handleArrayChange('tags', e.target.value)} placeholder="Web Security&#10;OWASP&#10;Pentesting"></textarea>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Abstract (Paragraphs)</label>
                                            <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="3" value={getArrayText('abstract')} onChange={e => handleArrayChange('abstract', e.target.value)}></textarea>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Who Should Attend</label>
                                            <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="3" value={getArrayText('whoShouldAttend')} onChange={e => handleArrayChange('whoShouldAttend', e.target.value)}></textarea>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Who Should Not Attend</label>
                                            <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="3" value={getArrayText('whoShouldNot')} onChange={e => handleArrayChange('whoShouldNot', e.target.value)}></textarea>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Audience Level</label>
                                            <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="3" value={getArrayText('audienceLevel')} onChange={e => handleArrayChange('audienceLevel', e.target.value)}></textarea>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Requirements</label>
                                            <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="3" value={getArrayText('requirements')} onChange={e => handleArrayChange('requirements', e.target.value)}></textarea>
                                        </div>

                                        <hr className="my-4 border-secondary w-100" />
                                        <div className="d-flex justify-content-between align-items-center w-100 mb-3">
                                            <h5 className="text-info m-0">Training Outline</h5>
                                            <button type="button" className="btn btn-sm btn-outline-info" onClick={addOutlineDay}>+ Add Day</button>
                                        </div>
                                        {Object.entries(formData.data.outline || {}).map(([day, items]) => (
                                            <div className="col-md-6 mb-3" key={day}>
                                                <label className="form-label">{day} (One item per line)</label>
                                                <textarea 
                                                    className="form-control bg-transparent text-white" 
                                                    style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                    rows="4" 
                                                    value={Array.isArray(items) ? items.join('\n') : ''} 
                                                    onChange={e => handleOutlineDayChange(day, e.target.value)}
                                                ></textarea>
                                            </div>
                                        ))}

                                        <hr className="my-4 border-secondary w-100" />
                                        <div className="d-flex justify-content-between align-items-center w-100 mb-3">
                                            <h5 className="text-info m-0">Trainers</h5>
                                            <button type="button" className="btn btn-sm btn-outline-info" onClick={addTrainer}>+ Add Trainer</button>
                                        </div>
                                        {(formData.data.trainers || []).map((trainer, idx) => (
                                            <div className="col-12 mb-3" key={idx}>
                                                <div className="p-3 rounded" style={{ border: '1px dashed rgba(255,255,255,0.2)' }}>
                                                    <div className="d-flex justify-content-between mb-3">
                                                        <h6 className="m-0 text-white-50">Trainer #{idx + 1}</h6>
                                                        <button type="button" className="btn btn-sm btn-danger py-0 px-2" onClick={() => removeTrainer(idx)}><i className="fas fa-trash"></i></button>
                                                    </div>
                                                    <div className="row g-3">
                                                        <div className="col-md-4">
                                                            <label className="form-label">Name</label>
                                                            <input type="text" className="form-control bg-transparent text-white border-secondary" value={trainer.name || ''} onChange={e => handleTrainerChange(idx, 'name', e.target.value)} />
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label className="form-label">Title</label>
                                                            <input type="text" className="form-control bg-transparent text-white border-secondary" value={trainer.title || ''} onChange={e => handleTrainerChange(idx, 'title', e.target.value)} />
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label className="form-label">Image Name (e.g. team-1-1.jpg)</label>
                                                            <input type="text" className="form-control bg-transparent text-white border-secondary" value={trainer.img || ''} onChange={e => handleTrainerChange(idx, 'img', e.target.value)} />
                                                        </div>
                                                        <div className="col-12">
                                                            <label className="form-label">Bio</label>
                                                            <textarea className="form-control bg-transparent text-white border-secondary" rows="2" value={trainer.bio || ''} onChange={e => handleTrainerChange(idx, 'bio', e.target.value)}></textarea>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}

                        {formData.type === 'page' && (
                            <div className="row g-3">
                                <h5>Hero Section</h5>
                                <div className="col-md-4">
                                    <label className="form-label">Eyebrow</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.eyebrow} onChange={e => setFormData({...formData, eyebrow: e.target.value})} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Title Accent</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.titleAccent} onChange={e => setFormData({...formData, titleAccent: e.target.value})} />
                                </div>
                                <div className="col-12">
                                    <label className="form-label">Subtitle</label>
                                    <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="2" value={formData.subtitle} onChange={e => setFormData({...formData, subtitle: e.target.value})}></textarea>
                                </div>
                                
                                <div className="col-md-6">
                                    <label className="form-label">Training Dates (Icon text)</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.trainingDates} onChange={e => setFormData({...formData, trainingDates: e.target.value})} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Event Venue (Icon text)</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.eventVenue} onChange={e => setFormData({...formData, eventVenue: e.target.value})} />
                                </div>

                                <hr className="my-4 border-secondary" />
                                <h5>Grid Section Header</h5>
                                <div className="col-md-4">
                                    <label className="form-label">Section Eyebrow</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.sectionEyebrow} onChange={e => setFormData({...formData, sectionEyebrow: e.target.value})} />
                                </div>
                                <div className="col-md-8">
                                    <label className="form-label">Section Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.sectionTitle} onChange={e => setFormData({...formData, sectionTitle: e.target.value})} />
                                </div>
                                <div className="col-12">
                                    <label className="form-label">Section Subtitle</label>
                                    <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="2" value={formData.sectionSubtitle} onChange={e => setFormData({...formData, sectionSubtitle: e.target.value})}></textarea>
                                </div>

                                <hr className="my-4 border-secondary" />
                                <h5>CTA Section</h5>
                                <div className="col-md-6">
                                    <label className="form-label">CTA Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.ctaTitle} onChange={e => setFormData({...formData, ctaTitle: e.target.value})} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">CTA Button Text</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.ctaButtonText} onChange={e => setFormData({...formData, ctaButtonText: e.target.value})} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">CTA Subtitle</label>
                                    <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="2" value={formData.ctaSubtitle} onChange={e => setFormData({...formData, ctaSubtitle: e.target.value})}></textarea>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">CTA Link</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.ctaButtonLink || ''} onChange={e => setFormData({...formData, ctaButtonLink: e.target.value})} />
                                </div>

                                <hr className="my-4 border-secondary" />
                                <h5>Detail Page Translations</h5>
                                <div className="col-md-3">
                                    <label className="form-label">Hero Eyebrow</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.heroEyebrow || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, heroEyebrow: e.target.value}})} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Back Button Text</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.backButtonText || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, backButtonText: e.target.value}})} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Register Button Text</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.registerButtonText || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, registerButtonText: e.target.value}})} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Register (Coming Soon) Text</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.registerComingSoonText || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, registerComingSoonText: e.target.value}})} />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Abstract Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.abstractTitle || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, abstractTitle: e.target.value}})} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Outline Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.outlineTitle || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, outlineTitle: e.target.value}})} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Trainers Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.trainersTitle || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, trainersTitle: e.target.value}})} />
                                </div>

                                <div className="col-md-3">
                                    <label className="form-label">Who Should Attend Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.whoShouldAttendTitle || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, whoShouldAttendTitle: e.target.value}})} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Who Should Not Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.whoShouldNotTitle || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, whoShouldNotTitle: e.target.value}})} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Audience Level Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.audienceLevelTitle || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, audienceLevelTitle: e.target.value}})} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Requirements Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.requirementsTitle || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, requirementsTitle: e.target.value}})} />
                                </div>
                                
                                <div className="col-md-4">
                                    <label className="form-label">Not Found Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.notFoundTitle || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, notFoundTitle: e.target.value}})} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Not Found Text</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.notFoundText || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, notFoundText: e.target.value}})} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Not Found Button</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.detailPage?.notFoundButtonText || ''} onChange={e => setFormData({...formData, detailPage: {...formData.detailPage, notFoundButtonText: e.target.value}})} />
                                </div>

                                <hr className="my-4 border-secondary" />
                                <h5>Listing Page Labels</h5>
                                <div className="col-md-6">
                                    <label className="form-label">Image Placeholder Text</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.listingPage?.imagePlaceholderText || ''} onChange={e => setFormData({...formData, listingPage: {...formData.listingPage, imagePlaceholderText: e.target.value}})} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Card Button Text</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.listingPage?.cardButtonText || ''} onChange={e => setFormData({...formData, listingPage: {...formData.listingPage, cardButtonText: e.target.value}})} />
                                </div>
                            </div>
                        )}

                        <div className="mt-4 d-flex gap-2">
                            <button type="submit" className="btn text-white" style={{ background: 'var(--eventflow-primary)' }}>Save Changes</button>
                            <button type="button" className="btn btn-outline-secondary text-white" onClick={() => setIsEditing(false)}>Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="card-header border-0 bg-transparent py-4 px-4 d-flex justify-content-between align-items-center">
                <h4 className="text-white m-0" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 700 }}>Manage Trainings</h4>
                <button className="btn btn-warning btn-sm" onClick={handleImportJSON}><i className="fas fa-file-import me-2"></i>Import from JSON</button>
            </div>
            
            <div className="card-body px-4 pb-4">
                <ul className="nav nav-pills mb-4" style={{ gap: '10px' }}>
                    <li className="nav-item">
                        <button className={`nav-link ${activeInnerTab === 'trainings' ? 'active bg-primary' : 'bg-transparent text-white'}`} style={{ border: '1px solid rgba(255,255,255,0.2)' }} onClick={() => setActiveInnerTab('trainings')}>Trainings List</button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${activeInnerTab === 'page' ? 'active bg-primary' : 'bg-transparent text-white'}`} style={{ border: '1px solid rgba(255,255,255,0.2)' }} onClick={() => setActiveInnerTab('page')}>Page Content</button>
                    </li>
                </ul>

                {activeInnerTab === 'trainings' && (
                    <>
                        <button className="btn mb-4 text-white" style={{ background: 'var(--eventflow-base)' }} onClick={handleAddTraining}>
                            <i className="fas fa-plus me-2"></i> Add New Training
                        </button>
                        <div className="table-responsive">
                            <table className="table table-dark table-hover align-middle mb-0" style={{ backgroundColor: 'transparent' }}>
                                <thead>
                                    <tr>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Image</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Title</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Instructor</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Level</th>
                                        <th style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)', textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {trainings.map(t => (
                                        <tr key={t.id}>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                {t.data.instructorImg && t.data.instructorImg.startsWith('http') ? (
                                                    <img src={t.data.instructorImg} alt={t.data.title} style={{ height: '40px', borderRadius: '4px', objectFit: 'cover' }} />
                                                ) : (
                                                    <span className="text-white-50"><i className="fas fa-image"></i></span>
                                                )}
                                            </td>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }} className="text-white fw-bold">{t.data.title}</td>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }} className="text-white-50">{t.data.instructor}</td>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                <span className="badge" style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--eventflow-primary)' }}>{t.data.level}</span>
                                            </td>
                                            <td style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'right' }}>
                                                <button className="btn btn-sm btn-outline-light me-2" onClick={() => handleEditTraining(t)}><i className="fas fa-edit"></i></button>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteTraining(t.id)}><i className="fas fa-trash"></i></button>
                                            </td>
                                        </tr>
                                    ))}
                                    {trainings.length === 0 && (
                                        <tr><td colSpan="5" className="text-center text-white-50 py-4" style={{ background: 'transparent' }}>No trainings found</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {activeInnerTab === 'page' && (
                    <div className="text-center py-5">
                        <i className="fas fa-file-alt text-white-50 mb-3" style={{ fontSize: '48px' }}></i>
                        <h5 className="text-white">Page Content</h5>
                        <p className="text-white-50 mb-4">Edit the titles and Call-To-Action text shown on the trainings section.</p>
                        <button className="btn text-white" style={{ background: 'var(--eventflow-base)' }} onClick={handleEditPage}>
                            <i className="fas fa-edit me-2"></i> Edit Page Content
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
